import { Viewer } from '../Viewer';
import { SVGObject, SVGRenderer } from 'three/examples/jsm/renderers/SVGRenderer';
import { SVGLabel } from './SVGObjects';
import * as THREE from 'three';
import { Object3D } from 'three';
import { SnapGeometry } from '../Tools/Picker';

type SnappingData = { vertex: THREE.Vector3; edge?: THREE.Line3; face?: any };

/**
 * SVGOverlay handles all necessary SVG overlays. Can be used for menus, shapes or other overlays needed.
 * These SVGOverlays are used in worldspace. For screen space see ./Overlay.
 * See https://www.w3.org/TR/SVG2/shapes.html#Introduction for documentation
 */
export class SVGOverlay {
    public get SVGRenderer() {
        return this._SVGrenderer;
    }
    private _SVGrenderer: SVGRenderer;
    /**
     * The scene that is used to render SVG Objects.
     */
    public get Scene(): THREE.Scene {
        return this._scene;
    }
    private _scene: THREE.Scene;
    private _parentContainer: HTMLElement;
    private _container: HTMLElement;

    /**
     * Returns whether the SVG overlay is being renderer.
     */
    public get IsActive() {
        return this._active;
    }
    private _active: any;

    private _renderDistance: number = 10000;
    /**
     * Returns whether a menu is active.
     */
    public get MenuActive(): boolean {
        return this._menuActive;
    }
    private _menuActive: boolean = false;
    private _activeMenu: SVGObject | null = null;
    private _activeSnappingSymbol: SVGObject | SVGObject[] = [];
    private _symbols: SVGObject[];

    /**
     * The color to use for strokes while making an overlay
     */
    private _strokeColor: string = 'hsl(121, 43%, 50%)';
    /**
     * The color to use for strokes while making an overlay
     */
    public get strokeColor(): string {
        return this._strokeColor;
    }
    /**
     * The color to use for strokes while making an overlay
     */
    public set strokeColor(v: string) {
        this._strokeColor = v;
    }

    /**
     * The color to use for fills while making an overlay
     */
    private _fillColor: string = 'hsl(213, 43%, 50%)';
    /**
     * The color to use for fills while making an overlay
     */
    public get fillColor(): string {
        return this._fillColor;
    }
    /**
     * The color to use for fills while making an overlay
     */
    public set fillColor(v: string) {
        this._fillColor = v;
    }

    /**
     * @constructor
     * @param _viewer The viewer for which this should be the SVG Overlay
     * @param width The width of the viewer
     * @param height The height of the overlay
     * @param  {HTMLElement} parentContainer The container of the viewer.
     */
    constructor(private _viewer: Viewer, parentContainer: HTMLElement, dimensions?: { width: number; height: number }) {
        this._parentContainer = parentContainer;

        this._symbols = [];
        this._SVGrenderer = new SVGRenderer();
        this._SVGrenderer.setClearColor(new THREE.Color(0xffffff), 0);
        // Alpha background is not yet added in three js (even though it's this simple...)
        this._SVGrenderer.domElement.style.backgroundColor = 'rgba(0,0,0,0)';
        if (dimensions) {
            this._SVGrenderer.setSize(dimensions.width, dimensions.height);
            this._SVGrenderer.setQuality('low');
        }

        this._container = document.createElement('SVGOverlay');
        this._container.style.zIndex = parentContainer.style.zIndex + 1;
        this._container.style.position = 'absolute';
        this._container.style.top = '0';
        this._container.style.left = '0';
        this._container.style.right = '0';
        this._container.style.bottom = '0';
        this._container.style.pointerEvents = 'none';
        this._container.style.background = 'rgba(0,0,0,0)';
        this._container.appendChild(this._SVGrenderer.domElement);

        parentContainer.appendChild(this._container);
        this._scene = new THREE.Scene();

        _viewer.ViewerEvents?.PointerEvent.on('move', (event) => {
            this.clearSnappingOverlay();
        });

        _viewer.ViewerEvents?.CameraEvent.on('change', (event) => {
            this.clearSnappingOverlay();
        });
    }

    setContainer(container: HTMLElement) {
        this._parentContainer = container;
        this._container = document.createElement('SVGOverlay');
        this._container.style.zIndex = container.style.zIndex + 1;
        this._container.style.position = 'absolute';
        this._container.style.top = '0';
        this._container.style.left = '0';
        this._container.style.right = '0';
        this._container.style.bottom = '0';
        this._container.style.pointerEvents = 'none';
        this._container.style.background = 'rgba(0,0,0,0)';
        this._container.appendChild(this._SVGrenderer.domElement);
        container.appendChild(this._container);
    }

    /**
     * Creates a menu for the given actions
     * @param pos The position where the menu should eb place. Can be screen space or world space
     * @param worldSpace If true the menu will be bound to a worldspace
     * @param size THe size of the individual buttons.
     * @param actions The actions to be presented. action.text will be the shown label.
     */
    createMenu(
        pos: { x: number; y: number; z?: number },
        size: { x: number; y: number },
        actions: { text: string; action: () => any }[],
        title?: string
    ): SVGObject {
        // Main
        const node = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        node.setAttributeNS(null, 'stroke', 'black');
        node.setAttributeNS(null, 'fill', 'grey');
        node.setAttributeNS(null, 'width', size.x.toString() + 'px');
        node.setAttributeNS(null, 'height', (size.y * actions.length).toString() + 'px');
        node.setAttributeNS(null, 'rx', '4px');
        node.setAttributeNS(null, 'ry', '4px');
        const Object = new SVGObject(node);

        // Title
        if (title) {
            const titleNode = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            titleNode.setAttributeNS(null, 'fill', 'white');
            titleNode.setAttributeNS(null, 'font', 'monospace ' + size.y * 0.7 + 'px');
            titleNode.setAttributeNS(null, 'paint-order', 'stroke');
            titleNode.setAttributeNS(null, 'stroke', 'black');
            titleNode.textContent = title;
            const textObject = new SVGObject(titleNode);
            Object.add(textObject);
        }

        // Actions
        let buttons = this.createMenuButtons(size, node, actions);
        buttons.forEach((button) => {
            Object.add(button);
        });
        Object.position.copy(new THREE.Vector3(pos.x, pos.y, pos.z));
        this._activeMenu = Object;
        this._scene.add(Object);
        this._menuActive = true;

        if (!this._active) this.startRender();
        return Object;
    }

    /**
     * Creates all the buttons for the given menu
     * @param size THe desired size for buttons
     * @param element THe parent HTMLElement or SVGElement
     * @param actions The array of buttons and actions
     */
    createMenuButtons(
        size: { x: number; y: number },
        element: SVGElement,
        actions: { text: string; action: () => void }[]
    ): Array<SVGObject> {
        let self = this;
        let buttons: SVGObject[] = [];
        actions.forEach((action, index) => {
            // Button
            // FOreign object is necessary to create a HTML element inside a SVG element
            let fo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
            fo.id = 'container';
            fo.setAttributeNS(null, 'x', (size.x * 0.025).toString());
            fo.setAttributeNS(null, 'y', (size.y * index + size.y * 0.05).toString());
            fo.setAttributeNS(null, 'height', size.y.toString() + 'px');
            fo.setAttributeNS(null, 'width', size.x.toString() + 'px');
            let buttonObj = new SVGObject(fo);

            // The actual button
            let button = document.createElement('button');
            button.style.width = (size.x * 0.95).toString() + 'px';
            button.style.height = (size.y * 0.9).toString() + 'px';
            button.style.borderRadius = '2.5px';
            button.style.pointerEvents = 'auto';
            button.style.background = 'lightgray';
            button.style.zIndex = fo.style.zIndex + 1;
            button.innerHTML = action.text;
            // Button needs to receive pointer events whereas the parent does not
            button.style.pointerEvents = 'auto';
            // Funcs
            button.addEventListener('click', action.action);
            button.addEventListener('click', self.clearMenu.bind(self));
            // Implementation of changing color when hovering over button
            button.addEventListener('mouseenter', onButtonEnter, { once: true });
            function onButtonEnter() {
                button.style.background = 'darkgrey';
                button.style.color = 'white';
                button.addEventListener('mouseleave', onButtonLeave, { once: true });
            }
            function onButtonLeave() {
                button.style.background = 'lightgray';
                button.style.color = 'black';
                button.addEventListener('mouseenter', onButtonEnter, { once: true });
            }
            // Disabling the default right click menu
            button.oncontextmenu = () => {
                return false;
            };

            fo.appendChild(button);
            buttons.push(buttonObj);
        });

        return buttons;
    }

    /**
     * Creates a label in worldspace for the given params
     * @param pos The desired world position
     * @param size The desired size of the text
     * @param text The desired text on the label
     * @returns {SVGObject} the SVGObject that was added to the SVG scene.
     */
    createLabel(
        size: number,
        text: string,
        pos?: { x: number; y: number; z: number },
        parameters?: Partial<{
            renderDistance: number;
            font: string;
            stroke: string;
            background: string;
            fontColor: string;
            border: string;
        }>
    ): SVGLabel {
        let svgLabel = new SVGLabel(size, text, parameters);
        if (pos) svgLabel.SetPosition(new THREE.Vector3(pos.x, pos.y, pos.z));
        //Scenes
        this._scene.add(svgLabel.Object);
        if (!this._active) this.startRender();
        return svgLabel;
    }

    createSnappingOverlay<T extends SnapGeometry = SnapGeometry.VERTEX>(data: SnappingData, color: number, snap: T) {
        if (!(data.vertex instanceof THREE.Vector3)) return null;
        if (snap === SnapGeometry.VERTEX) {
            let symbol = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            symbol.id = 'Vertex';
            symbol.style.fill = this.strokeColor;
            symbol.setAttribute('r', 4 + 'px');
            let vertexObject = new SVGObject(symbol);
            vertexObject.position.copy(data.vertex);
            if (Array.isArray(this._activeSnappingSymbol)) this._scene.remove(...this._activeSnappingSymbol);
            else if (this._activeSnappingSymbol) this._scene.remove(this._activeSnappingSymbol);

            this._activeSnappingSymbol = vertexObject;
            this._scene.add(vertexObject);
            if (!this._active) this.startRender();
        } else if (snap === SnapGeometry.EDGE && data.edge instanceof THREE.Line3) {
            //Vertex
            let vertexSymbol = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            vertexSymbol.id = 'Vertex';
            vertexSymbol.style.fill = this.strokeColor;
            vertexSymbol.setAttribute('r', 4 + 'px');
            let vertexObject = new SVGObject(vertexSymbol);
            vertexObject.position.copy(data.vertex);
            // Edge
            let center = data.edge.getCenter(new THREE.Vector3());
            let edgeSymbol = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
            edgeSymbol.id = 'edge';
            edgeSymbol.style.fill = this.fillColor;
            edgeSymbol.style.stroke = this.strokeColor;
            edgeSymbol.style.strokeWidth = '2px';
            let startScreen = data.edge.start.clone().project(this._viewer.currentCamera!);
            let endScreen = data.edge.end.clone().project(this._viewer.currentCamera!);
            let centerScreen = center.clone().project(this._viewer.currentCamera!);
            let width = this._viewer._container.clientWidth / 2;
            let height = -this._viewer._container.clientHeight / 2;

            let pos1 = { x: (startScreen.x - centerScreen.x) * width, y: (startScreen.y - centerScreen.y) * height };
            let pos2 = { x: (endScreen.x - centerScreen.x) * width, y: (endScreen.y - centerScreen.y) * height };

            edgeSymbol.setAttribute('points', pos1.x + ',' + pos1.y + ' 0,0 ' + pos2.x + ',' + pos2.y);
            let edgeObject = new SVGObject(edgeSymbol);
            edgeObject.position.copy(center);

            //Snapping Object
            if (Array.isArray(this._activeSnappingSymbol)) this._scene.remove(...this._activeSnappingSymbol);
            else if (this._activeSnappingSymbol) this._scene.remove(this._activeSnappingSymbol);
            this._activeSnappingSymbol = [edgeObject, vertexObject];
            this._scene.add(...this._activeSnappingSymbol);
            if (!this._active) this.startRender();
        } else if (snap === SnapGeometry.FACE && data.face) {
            //Vertex
            let vertexSymbol = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            vertexSymbol.id = 'Vertex';
            vertexSymbol.style.fill = this.strokeColor;
            vertexSymbol.setAttribute('r', 4 + 'px');
            let vertexObject = new SVGObject(vertexSymbol);
            vertexObject.position.copy(data.vertex);

            //Face
            let pathObjects: SVGObject[] = [];
            if (data.face.vertices.length > 0) {
                let faceObject = this.createSnappingSymbol(data.face.vertices, data.face.center);
                faceObject.position.copy(data.face.center);
                pathObjects = [faceObject];
            }

            // Snapping Objects
            if (Array.isArray(this._activeSnappingSymbol)) this._scene.remove(...this._activeSnappingSymbol);
            else if (this._activeSnappingSymbol) this._scene.remove(this._activeSnappingSymbol);
            this._activeSnappingSymbol = [vertexObject, ...pathObjects];
            this._scene.add(...this._activeSnappingSymbol);

            if (!this._active) this.startRender();
        }
    }

    createSnappingSymbol(vertices, center): SVGObject {
        const node = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        let svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        node.id = 'face';
        node.style.fill = this.fillColor;
        node.style.stroke = this.strokeColor;
        node.style.strokeWidth = '2px';
        node.style.opacity = '50%';

        // Pos
        let centerScreen = center.clone().project(this._viewer.currentCamera);
        let width = this._viewer._container.clientWidth / 2;
        let height = -this._viewer._container.clientHeight / 2;

        vertices.forEach((vertex) => {
            let screenPos = vertex.clone().project(this._viewer.currentCamera);
            let svgPoint = svg.createSVGPoint();
            svgPoint.x = (screenPos.x - centerScreen.x) * width;
            svgPoint.y = (screenPos.y - centerScreen.y) * height;
            node.points.appendItem(svgPoint);
        });

        return new SVGObject(node);
    }

    clearSnappingOverlay() {
        if (Array.isArray(this._activeSnappingSymbol)) this._scene.remove(...this._activeSnappingSymbol);
        else if (this._activeSnappingSymbol) this._scene.remove(this._activeSnappingSymbol);
    }

    createOverlaySymbol(symbol: 'circle' | 'rect' | 'angle', pos: THREE.Vector3, fill?: string, stroke?: string): SVGObject {
        var node;
        if (symbol === 'circle' || symbol === 'rect') {
            node = document.createElementNS('http://www.w3.org/2000/svg', symbol);
            node.id = 'Vertex';
            node.setAttribute('r', 4 + 'px');
        } else {
            node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            node.id = 'Symbol';
            node.setAttribute('d', this.getPath(symbol));
        }
        node.style.stroke = stroke ? stroke : 'none';
        node.style.fill = fill ? fill : 'none';

        let object = new SVGObject(node);
        object.position.copy(pos);
        this._symbols.push(object);
        this._scene.add(object);
        if (!this._active) this.startRender();

        return object;
    }

    getPath(symbol: 'angle' | 'center') {
        // See https://yqnn.github.io/svg-path-editor/ for path editor
        switch (symbol) {
            case 'angle':
                return 'm10 0a5 5 90 10-10 0A5 5 90 1010 0M10-8 0 6M10-6 0 8';
            case 'center':
                return 'M0 0-10 10H10L-10-10 10-10 0 0';
        }
    }

    clearSymbols() {
        this._symbols.forEach((symbol) => {
            this._scene.remove(symbol);
        });
    }

    getTextWidth(text: string, font: string) {
        let element = document.createElement('canvas');
        let context = element.getContext('2d')!;
        context.font = font;
        let width = context.measureText(text).width;
        element.remove();
        return width;
    }

    /**
     * Sets the renderer active and  renders the first frame.
     */
    startRender() {
        this._active = true;
        this.render();
    }

    /**
     * Deactivates the renderer
     */
    stopRender() {
        this._active = false;
    }

    /**
     * Renders the SVG scene for this frame. The loop is controlled by the Viewer. To activate the renderloop use SVGOverlay.StartRender().
     */
    render() {
        if (!this._active) return;

        /**
         *  Naive solution to apply a max renderdistance for each child
         * @todo Find implementation that's more efficient. (LOD doesnt work for SVGObject.)
         */
        this._scene.traverse((child) => {
            if (child instanceof SVGObject && child.userData.renderDistance) {
                let pos: THREE.Vector3 = new THREE.Vector3();
                child.getWorldPosition(pos);
                child.visible =
                    pos.distanceToSquared(this._viewer.currentCamera!.position) <
                    child.userData.renderDistance * child.userData.renderDistance;
            }
        });

        this._SVGrenderer.render(this._scene, this._viewer.currentCamera!);
        // Alpha background is not yet added in three js (even though it's this simple...)
        this._SVGrenderer.domElement.style.backgroundColor = 'rgba(0,0,0,0)';
    }

    /**
     * Removes the active menu
     */
    clearMenu() {
        if (!this._activeMenu) return;

        this._scene.remove(this._activeMenu);
        this.render();
        this._menuActive = false;
    }

    /**
     * Clears everything in the overlay
     */
    clear() {
        let self = this;
        let lastObject;
        try {
            let children = this._scene.children;
            while (children.length > 0) {
                let child = children[0];
                child.traverse((object) => self.disposeObject(object));
                if (child.type !== 'Scene') {
                    this._scene?.remove(child);
                }
            }
        } catch (error) {
            console.error('SVGOverlay::Clear() (lastObject:' + lastObject.type + ') Error:' + error);
        }
    }

    cleanUp() {
        this.stopRender();
        this.clear();
        this._SVGrenderer.clear();
        this._container?.remove();
    }

    /**
     * Removes the given obejct from the SVG scene
     * @param objects
     */
    remove(objects: SVGObject | Array<SVGObject>) {
        if (Array.isArray(objects)) {
            objects.forEach((object) => {
                this._scene.remove(object);
            });
        } else {
            this._scene.remove(objects);
        }
    }

    /**
     * Disposes the desired object
     * @param object
     */
    private disposeObject(object: any) {
        if (!object) return;
        if (object.geometry) {
            object.geometry.dispose();
        }
        if (object.material) {
            if (Array.isArray(object.material)) {
                object.material.forEach((mat) => {
                    mat.dispose();
                });
            } else {
                object.material.dispose();
            }
        }
    }

    handleResize() {
        if (!this._parentContainer) return;

        this._SVGrenderer.setSize(this._parentContainer.clientWidth, this._parentContainer.clientHeight);
    }
}
